import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { apiClient } from '@services/api';
import Button from '@components/Button';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import Moment from 'react-moment';

const STATUS_TABS = [
  { key: 'active',        label: 'Ativos' },
  { key: 'need_reply',    label: 'Responder',          urgent: true },
  { key: 'awaiting_team', label: 'Aguardando resposta' },
  { key: 'all',           label: 'Todos' },
  { key: 'resolved',      label: 'Resolvidos' },
  { key: 'closed',        label: 'Fechados' },
  { key: 'budgets',       label: 'Orçamentos' },
];

const PRIORITY_ICON = {
  urgent: { icon: 'arrow_upward', color: '#ef4444' },
  high:   { icon: 'arrow_upward', color: '#f59e0b' },
  normal: { icon: 'remove',       color: '#38bdf8' },
  low:    { icon: 'arrow_downward', color: '#64748b' },
};

const Index = (props) => {
  const history = useHistory();
  const [view, setView]         = useState('active');
  const [tickets, setTickets]   = useState([]);
  const [budgetsList, setBudgetsList] = useState([]);
  const [counts, setCounts]     = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => { props.setShowMenu(false); }, []);

  // "Ok, ciente" — fecha tickets de checagem (orçamento aprovado/recusado) sem abrir
  const quickClose = (ticketId) => {
    apiClient.httpInstance
      .post(`/tickets/${ticketId}/transition`, { action: 'set_status', status: 'closed' })
      .then(() => {
        setTickets(prev => prev.filter(t => t.id !== ticketId));
        setCounts(prev => ({
          ...prev,
          [view]: Math.max(0, (prev[view] || 1) - 1),
          closed: (prev.closed || 0) + 1,
        }));
      })
      .catch(() => alert('Não foi possível fechar o ticket.'));
  };

  useEffect(() => {
    setLoading(true);
    if (view === 'budgets') {
      apiClient.httpInstance
        .get('/presales', {
          params: { customer_status: 'approved,refused', sort: 'customer_responded_at', pageSize: 20 }
        })
        .then(res => {
          if (res.status === 200) {
            const data = res.data.data || res.data || [];
            setBudgetsList(Array.isArray(data) ? data : []);
          }
        })
        .catch(() => setBudgetsList([]))
        .finally(() => setLoading(false));
    } else {
      apiClient.httpInstance
        .get('/tickets', { params: { view } })
        .then(res => {
          if (res.status === 200) {
            setTickets(res.data.data);
            setCounts(res.data.counts);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [view]);

  return (
    <div className="px-7 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between mt-2 text-2xl" style={{ color: props.theme.color }}>
        <span>Tickets</span>
        <Button
          textcolor="#fff"
          padding="6px 15px"
          margintop="0px"
          width="160"
          onClick={() => history.push('/admin/tickets/novo')}
          customcolor={props.theme.color}
        >
          <Icon className="mr-2">add</Icon> Novo ticket
        </Button>
      </div>

      {/* Abas */}
      <div className="flex flex-wrap mt-4 border-b" style={{ borderColor: '#2a3f5f' }}>
        {STATUS_TABS.map(tab => {
          const isActive = view === tab.key;
          const accentColor = tab.urgent ? '#f59e0b' : props.theme.color;
          const hasCount = counts[tab.key] !== undefined && counts[tab.key] > 0;
          return (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              className="px-4 py-2 text-sm font-medium transition-colors"
              style={{
                color: isActive ? accentColor : (tab.urgent && hasCount ? '#f59e0b' : '#94a3b8'),
                borderBottom: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              {tab.label}
              {counts[tab.key] !== undefined && (
                <span
                  className="ml-2 px-2 py-0.5 rounded-full text-xs"
                  style={{
                    background: isActive ? accentColor : (tab.urgent && hasCount ? '#f59e0b' : '#1e3a5f'),
                    color: '#fff',
                  }}
                >
                  {counts[tab.key]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lista */}
      <div className="mt-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <CircularProgress style={{ color: props.theme.color }} />
          </div>
        ) : view === 'budgets' ? (
          budgetsList.length === 0 ? (
            <div className="flex flex-col items-center py-16" style={{ color: '#64748b' }}>
              <Icon style={{ fontSize: 48 }}>receipt_long</Icon>
              <p className="mt-3 text-sm">Nenhum orçamento respondido recentemente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {budgetsList.map(b => (
                <BudgetRow
                  key={b.id}
                  budget={b}
                  theme={props.theme}
                  onClick={() => history.push(`/admin/orcamentos/${b.id}`)}
                />
              ))}
            </div>
          )
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center py-16" style={{ color: '#64748b' }}>
            <Icon style={{ fontSize: 48 }}>confirmation_number</Icon>
            <p className="mt-3 text-sm">Nenhum ticket encontrado.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets.map(t => (
              <TicketRow
                key={t.id}
                ticket={t}
                theme={props.theme}
                onClick={() => history.push(`/admin/tickets/${t.id}`)}
                onQuickClose={quickClose}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BudgetRow = ({ budget: b, theme, onClick }) => {
  const isApproved = b.customer_status === 'approved';
  const isRefused  = b.customer_status === 'refused';

  const badgeStyle = isApproved
    ? { background: '#166534', color: '#bbf7d0', border: '1px solid #22c55e' }
    : isRefused
    ? { background: '#7f1d1d', color: '#fecaca', border: '1px solid #ef4444' }
    : { background: '#172B4D', color: '#8ba5c4', border: '1px solid #1e3a5a' };

  const badgeText = isApproved ? 'Aprovado pelo cliente' : isRefused ? 'Recusado pelo cliente' : b.customer_status || '—';

  const respondedAt = b.customer_responded_at || b.updated_at;

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg"
      style={{ background: '#172B4D', border: '1px solid #1e3a5a' }}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon style={{ color: isApproved ? '#22c55e' : isRefused ? '#ef4444' : '#8ba5c4', fontSize: 20, marginTop: 2 }}>
          {isApproved ? 'check_circle' : isRefused ? 'cancel' : 'receipt_long'}
        </Icon>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium" style={{ color: '#e8f0fb' }}>
              {b.references || `#${b.id}`}
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={badgeStyle}
            >
              {badgeText}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs" style={{ color: '#8ba5c4' }}>
            {(b.name || b.customer_name) && <span>{b.name || b.customer_name}</span>}
            {respondedAt && (
              <span>
                <Moment format="DD/MM/YYYY HH:mm" date={respondedAt} />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
        <button
          onClick={onClick}
          style={{
            background: '#2a4f72',
            color: '#e8f0fb',
            border: 'none',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon style={{ fontSize: 16 }}>open_in_new</Icon>
          Ver orçamento
        </button>
      </div>
    </div>
  );
};

const TicketRow = ({ ticket: t, theme, onClick, onQuickClose }) => {
  const pri = PRIORITY_ICON[t.priority] || PRIORITY_ICON.normal;
  // Tickets de checagem (vendas: orçamento aprovado/recusado, pagamento confirmado) ainda abertos
  const isCheckTicket = t.category === 'sales' && !['closed', 'resolved'].includes(t.status);
  return (
    <div
      onClick={onClick}
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg cursor-pointer transition-all"
      style={{
        background: '#0f2340',
        border: t.waiting_you ? `1px solid ${theme.color}` : '1px solid #1e3a5f',
      }}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {/* Prioridade */}
        <Icon style={{ color: pri.color, fontSize: 18, marginTop: 2 }}>{pri.icon}</Icon>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white">{t.subject}</span>
            {t.waiting_you && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: theme.color, color: '#fff' }}>
                Resposta nova
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs" style={{ color: '#64748b' }}>
            <span>#{t.id}</span>
            <span style={{ color: '#94a3b8' }}>{t.category_label}</span>
            <span>{t.opener_name}</span>
            {t.assignee_name && <span>→ {t.assignee_name}</span>}
            <span>{t.last_reply_at || t.created_rel}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
        {/* Ok, ciente — fecha ticket de checagem sem abrir */}
        {isCheckTicket && onQuickClose && (
          <button
            onClick={(e) => { e.stopPropagation(); onQuickClose(t.id); }}
            title="Marcar como ciente e fechar"
            style={{
              background: '#166534',
              color: '#bbf7d0',
              border: '1px solid #22c55e',
              borderRadius: 8,
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            <Icon style={{ fontSize: 15 }}>done_all</Icon>
            Ok, ciente
          </button>
        )}
        {/* Rating */}
        {t.rating && (
          <span className="text-lg" title={t.rating_label}>{t.rating_emoji}</span>
        )}
        {/* Status badge */}
        <span
          className="text-xs px-2 py-1 rounded-full whitespace-nowrap"
          style={{ background: t.status_color + '22', color: t.status_color, border: `1px solid ${t.status_color}44` }}
        >
          {t.status_label}
        </span>
        {/* Mensagens */}
        <div className="flex items-center gap-1 text-xs" style={{ color: '#64748b' }}>
          <Icon style={{ fontSize: 14 }}>chat_bubble_outline</Icon>
          <span>{t.messages_count}</span>
        </div>
        <Icon style={{ color: '#64748b', fontSize: 18 }}>chevron_right</Icon>
      </div>
    </div>
  );
};

export default Index;
